/**
 * iter307 · Global axios 5xx reporter
 * ─────────────────────────────────────────────────────────────────────
 * Installs a single response interceptor on the shared axios default
 * instance so every 5xx response — regardless of which component
 * fires it — is auto-mirrored into the Health Watchdog. Runs
 * exactly once (idempotent) even under React StrictMode double-mount.
 *
 * We deliberately DO NOT alter the promise chain: we tap the error,
 * fire the report keepalive-style, and re-throw so callers keep
 * behaving exactly the same.
 */
import axios from 'axios';

let installed = false;

export function installHealthReporter() {
  if (installed) return;
  installed = true;

  const backend = process.env.REACT_APP_BACKEND_URL || '';
  if (!backend) return;

  axios.interceptors.response.use(
    (r) => r,
    (err) => {
      try {
        const status = err?.response?.status;
        // Only track true server crashes — skip network errors + 4xx
        // (those are usually intentional client-side flows like 401 /
        // 403 / 404).
        if (status && status >= 500 && status <= 599) {
          const path = (() => {
            try {
              const u = new URL(err.config?.url || '', backend);
              return u.pathname + (u.search || '');
            } catch (_) { return err.config?.url || ''; }
          })();
          const detail = err.response?.data?.detail || err.response?.data?.message || err.message || 'server error';
          fetch(`${backend}/api/health/report-error`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              message:     String(detail).slice(0, 1000),
              path,
              kind:        'xhr',
              status_code: status,
              user_agent:  navigator.userAgent,
            }),
            keepalive: true,
            credentials: 'include',
          }).catch(() => { /* best-effort */ });
        }
      } catch (_) { /* interceptor must never throw */ }
      return Promise.reject(err);
    },
  );
}
